import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, ArrowRight, BookOpen, Filter } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import useSEO from '../hooks/useSEO';
import useJsonLd from '../hooks/useJsonLd';
import { PAGE_SEO, SITE_CONFIG } from '../data/seoConfig';
import { BLOG_POSTS } from '../data/blog';

export default function Blog() {
  useSEO(PAGE_SEO.blog);

  useJsonLd('blog-list-schema', [
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      url: `${SITE_CONFIG.url}/blog`,
      name: 'Ransen Marketing Blog',
      description: PAGE_SEO.blog.description,
      publisher: { '@id': `${SITE_CONFIG.url}/#organization` },
      blogPost: BLOG_POSTS.map((p) => ({
        '@type': 'BlogPosting',
        headline: p.title,
        url: `${SITE_CONFIG.url}/blog/${p.slug}`,
        datePublished: p.date,
        image: p.image,
        author: { '@type': 'Organization', name: 'Ransen' },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_CONFIG.url },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_CONFIG.url}/blog` },
      ],
    },
  ]);

  const [activeCategory, setActiveCategory] = useState('All');

  // Build category list with post counts
  const categories = useMemo(() => {
    const counts = BLOG_POSTS.reduce((acc, p) => {
      acc[p.category] = (acc[p.category] || 0) + 1;
      return acc;
    }, {});
    const list = Object.keys(counts).sort();
    return [{ name: 'All', count: BLOG_POSTS.length }, ...list.map((c) => ({ name: c, count: counts[c] }))];
  }, []);

  const filtered = useMemo(() => {
    if (activeCategory === 'All') return BLOG_POSTS;
    return BLOG_POSTS.filter((p) => p.category === activeCategory);
  }, [activeCategory]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div data-testid='blog-index-page'>
      <PageHeader
        eyebrow='Blog & Insights'
        title={<><span className='text-white'>Marketing trends & </span><span className='text-brand-gradient'>expert playbooks</span></>}
        description='Deep-dive content on the fast-moving frontier of AI-powered marketing — LLM optimization, server-side tracking, Meta Advantage+, programmatic SEO, and more.'
        crumbs={[{ label: 'Blog' }]}
      />

      {/* Category filter */}
      <section className='pt-4 pb-2' data-testid='blog-category-filter'>
        <div className='max-w-6xl mx-auto px-6'>
          <div className='flex items-center gap-3 flex-wrap'>
            <div className='inline-flex items-center gap-1.5 text-white/50 text-[12px] uppercase tracking-wider font-accent mr-1'>
              <Filter className='w-3.5 h-3.5' /> Filter
            </div>
            {categories.map((cat) => {
              const isActive = cat.name === activeCategory;
              return (
                <button
                  key={cat.name}
                  type='button'
                  onClick={() => setActiveCategory(cat.name)}
                  data-testid={`blog-filter-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-[13px] font-medium transition-colors ${
                    isActive
                      ? 'bg-brand/20 border-brand/50 text-white'
                      : 'bg-white/[0.03] border-white/10 text-white/60 hover:text-white hover:border-white/25'
                  }`}
                >
                  {cat.name}
                  <span className={`inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 rounded-full text-[10.5px] font-semibold ${
                    isActive ? 'bg-white/15 text-white' : 'bg-white/[0.04] text-white/50'
                  }`}>
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Empty state */}
      {filtered.length === 0 && (
        <section className='py-24 text-center'>
          <p className='text-white/60'>No articles in this category yet.</p>
        </section>
      )}

      {/* Featured post */}
      {featured && (
        <AnimatePresence mode='wait'>
          <motion.section
            key={`featured-${activeCategory}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
            className='py-12'
          >
            <div className='max-w-6xl mx-auto px-6'>
              <Link to={`/blog/${featured.slug}`} className='glass-card rounded-2xl overflow-hidden group grid md:grid-cols-2' data-testid='blog-featured-post'>
                <div className='relative aspect-[16/10] md:aspect-auto overflow-hidden'>
                  <img src={featured.image} alt={featured.title} className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-105' />
                  <div className='absolute inset-0 bg-gradient-to-r from-void via-void/50 to-transparent md:from-void md:via-transparent md:to-transparent' />
                  <div className='absolute top-4 left-4 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white/85 text-[11px] tracking-wider uppercase font-accent'>Featured</div>
                </div>
                <div className='p-8 md:p-10 flex flex-col justify-center'>
                  <div className='eyebrow-pill' style={{ fontSize: 11 }}>{featured.category}</div>
                  <h2 className='mt-4 font-heading text-2xl md:text-3xl font-bold text-white group-hover:text-brand transition-colors leading-tight'>{featured.title}</h2>
                  <p className='mt-4 text-white/65 text-[15px] leading-relaxed font-body'>{featured.excerpt}</p>
                  <div className='mt-6 flex flex-wrap gap-4 text-white/50 text-[13px]'>
                    <span className='inline-flex items-center gap-1.5'><Calendar className='w-3.5 h-3.5' /> {featured.date}</span>
                    <span className='inline-flex items-center gap-1.5'><Clock className='w-3.5 h-3.5' /> {featured.read}</span>
                    <span className='inline-flex items-center gap-1.5'><BookOpen className='w-3.5 h-3.5' /> {featured.author}</span>
                  </div>
                  <div className='mt-6 inline-flex items-center gap-2 text-brand font-medium text-sm group-hover:gap-3 transition-all'>
                    Read the full playbook <ArrowRight className='w-4 h-4' />
                  </div>
                </div>
              </Link>
            </div>
          </motion.section>
        </AnimatePresence>
      )}

      {/* Grid of remaining posts */}
      {rest.length > 0 && (
        <section className='py-8 pb-24 border-t border-brand/10'>
          <div className='max-w-6xl mx-auto px-6'>
            <motion.div
              key={`grid-${activeCategory}`}
              initial='hidden'
              animate='show'
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.05 } },
              }}
              className='grid md:grid-cols-2 lg:grid-cols-3 gap-6'
            >
              {rest.map((post) => (
                <motion.div
                  key={post.slug}
                  variants={{
                    hidden: { opacity: 0, y: 16 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.2, 0.7, 0.2, 1] } },
                  }}
                >
                  <Link to={`/blog/${post.slug}`} className='glass-card rounded-2xl overflow-hidden group flex flex-col h-full' data-testid={`blog-post-card-${post.slug}`}>
                    <div className='relative aspect-[16/9] overflow-hidden'>
                      <img src={post.image} alt={post.title} className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-105' />
                      <div className='absolute inset-0 bg-gradient-to-t from-void via-void/40 to-transparent' />
                      <div className='absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white/85 text-[10.5px] tracking-wider uppercase font-accent'>{post.category}</div>
                    </div>
                    <div className='p-6 flex-1 flex flex-col'>
                      <h3 className='font-heading text-lg font-bold text-white group-hover:text-brand transition-colors leading-snug'>{post.title}</h3>
                      <p className='mt-3 text-white/60 text-[13.5px] leading-relaxed font-body line-clamp-3'>{post.excerpt}</p>
                      <div className='mt-auto pt-5 flex items-center justify-between text-white/45 text-[12.5px]'>
                        <span className='inline-flex items-center gap-1.5'><Clock className='w-3 h-3' /> {post.read}</span>
                        <span className='text-brand'>{post.date}</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
}
